import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { app } from '../../firebase'; 

import NavbarDashboard from '../Dashboard/NavbarDashboard';
import { Box, Button, TextField } from '@mui/material';
import ResponsiveDrawer from '../Dashboard/Sidebar';
import { Refresh as RefreshIcon, Download as DownloadIcon } from '@mui/icons-material';
import { getRecordDate, toDateSafe } from '../../utils/financialYear';

const firestore = getFirestore(app);

const UserStatus = ({ userPermissions }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const getTimestampFromUser = (user) => getRecordDate(user) || new Date(0);

  const formatDate = (user) => {
    const date = getRecordDate(user);
    return date ? date.toLocaleString() : 'NA';
  };

  const fetchData = async () => {
    try {
      const usersSnapshot = await getDocs(collection(firestore, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const tdsFormSnapshot = await getDocs(collection(firestore, 'tds_forms'));
      const tdsFormMap = new Map(
        tdsFormSnapshot.docs.map((formDoc) => {
          const data = formDoc.data();
          return [
            data.userId ?? formDoc.id,
            {
              ...data,
              timestamp: toDateSafe(data.timestamp),
            },
          ];
        }),
      );

      const tdsStateSnapshot = await getDocs(collection(firestore, 'tds_state'));
      const tdsStateMap = new Map(
        tdsStateSnapshot.docs.map((stateDoc) => [stateDoc.id, stateDoc.data()]),
      );

      const mergedData = usersData.map(user => {
        const tdsState = tdsStateMap.get(user.id) || {};
        const tdsForm = tdsFormMap.get(user.id) || {};
        const {
          email: _formEmail,
          phone: _formPhone,
          id: _formId,
          userId: _userId,
          timestamp,
          ...restTdsForm
        } = tdsForm;

        return {
          ...user,
          ...restTdsForm,
          ...tdsState,
          timestamp: timestamp || user.timestamp || null,
        };
      });

      const sortedData = mergedData.sort((a, b) => {
        const timeA = getTimestampFromUser(a).getTime();
        const timeB = getTimestampFromUser(b).getTime();
        return timeB - timeA;
      });

      setUsers(sortedData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 600000); 
    return () => clearInterval(interval);
  }, []);

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone && user.phone.includes(searchTerm));

    const userDate = getTimestampFromUser(user);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);

    const matchesDate =
      (!start || userDate >= start) &&
      (!end || userDate <= end);

    return matchesSearch && matchesDate;
  });

  const downloadCSV = () => {
    const headers = [
      "Sr No.",
      "User Name",
      "Date & Time",
      "Email ID",
      "Phone No.",
      "TDS Form Status",
      "Income Tax Website Registration",
      "26 AS Upload",
      "ITR File Status",
      "Payment Status",
      "E-verification"
    ];

    const rows = filteredUsers.map((user, index) => [
      index + 1,
      user.name || '',
      `"${formatDate(user)}"`,
      user.email || '',
      user.phone || '',
      user.tdsformfilluped ? 'Complete' : 'Incomplete',
      user.incomesiteregistration ? 'Complete' : 'Incomplete',
      user.tdsA26s ? 'Complete' : 'Incomplete',
      user.fileTds ? 'Complete' : 'Incomplete',
      user.paymentstatus ? 'Complete' : 'Incomplete',
      user.finalotpstatus ? 'Complete' : 'Incomplete',
    ]);

    let csvContent = "data:text/csv;charset=utf-8," 
      + [headers, ...rows].map(e => e.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = "User_Status_Report.csv";
    link.click();
  };

  return (
    <div className="user-status-page">
      <NavbarDashboard />
      <Box sx={{ display: 'flex', width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
        <ResponsiveDrawer userPermissions={userPermissions} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            maxWidth: '100%',
            overflowX: 'hidden',
            overflowY: 'auto',
            p: { xs: 1, sm: 2, md: 3 },
            pt: { xs: 9, sm: 10, md: 11 },
            pb: { xs: 2, sm: 3 },
          }}
        >
          <div className="user-status-toolbar">
            <TextField
              type="text"
              placeholder="Search by username or email or phone..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              size="small"
              fullWidth
              sx={{ flex: '1 1 220px', minWidth: 0 }}
            />
            <TextField
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              size="small"
              sx={{ flex: { xs: '1 1 100%', sm: '0 1 160px' }, minWidth: { xs: 0, sm: 140 } }}
            />
            <TextField
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              size="small"
              sx={{ flex: { xs: '1 1 100%', sm: '0 1 160px' }, minWidth: { xs: 0, sm: 140 } }}
            />
            <Button
              variant="contained"
              onClick={fetchData}
              startIcon={<RefreshIcon />}
              sx={{
                flex: { xs: '1 1 calc(50% - 5px)', sm: '0 0 auto' },
                backgroundColor: 'lightgreen',
                color: '#000',
                '&:hover': { backgroundColor: '#7CFF7C' },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              onClick={downloadCSV}
              startIcon={<DownloadIcon />}
              sx={{
                flex: { xs: '1 1 calc(50% - 5px)', sm: '0 0 auto' },
                backgroundColor: '#2196F3',
              }}
            >
              Download Report
            </Button>
          </div>

          {/* Single scroll container; Sr No + User Name stay sticky */}
          <div className="user-status-scroll">
            <table className="user-status-table">
              <thead>
                <tr>
                  <th className="sticky-sr">Sr No.</th>
                  <th className="sticky-name">User Name</th>
                  <th>Date & Time</th>
                  <th>Email ID</th>
                  <th>Phone No.</th>
                  <th>TDS Form Status</th>
                  <th>Income Tax Website Registration</th>
                  <th>26 AS Upload</th>
                  <th>ITR File Status</th>
                  <th>Payment Status</th>
                  <th>E-verification</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <tr key={user.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                    <td className="sticky-sr">{index + 1}</td>
                    <td className="sticky-name">{user.name || 'N/A'}</td>
                    <td>{formatDate(user)}</td>
                    <td>{user.email || 'N/A'}</td>
                    <td>{user.phone || 'N/A'}</td>
                    <td className={user.tdsformfilluped ? 'status-ok' : 'status-bad'}>
                      {user.tdsformfilluped ? 'Complete' : 'Incomplete'}
                    </td>
                    <td className={user.incomesiteregistration ? 'status-ok' : 'status-bad'}>
                      {user.incomesiteregistration ? 'Complete' : 'Incomplete'}
                    </td>
                    <td className={user.tdsA26s ? 'status-ok' : 'status-bad'}>
                      {user.tdsA26s ? 'Complete' : 'Incomplete'}
                    </td>
                    <td className={user.fileTds ? 'status-ok' : 'status-bad'}>
                      {user.fileTds ? 'Complete' : 'Incomplete'}
                    </td>
                    <td className={user.paymentstatus ? 'status-ok' : 'status-bad'}>
                      {user.paymentstatus ? 'Complete' : 'Incomplete'}
                    </td>
                    <td className={user.finalotpstatus ? 'status-ok' : 'status-bad'}>
                      {user.finalotpstatus ? 'Complete' : 'Incomplete'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <div className="user-status-empty">
                No users found matching your criteria
              </div>
            )}
          </div>
        </Box>
      </Box>
    </div>
  );
};

export default UserStatus;

const css = `
.user-status-page {
  width: 100%;
  max-width: 100vw;
  overflow: hidden;
}

.user-status-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
  width: 100%;
}

/* ONE scroll box — Sr No + User Name stick left; headers stick on vertical scroll */
.user-status-scroll {
  width: 100%;
  max-width: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  max-height: calc(100dvh - 180px);
  min-height: 420px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
}

.user-status-table {
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 1280px;
  table-layout: auto;
}

.user-status-table th,
.user-status-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  border-right: 1px solid #eee;
  white-space: nowrap;
  font-size: 13px;
  vertical-align: middle;
}

/* Sticky header row on vertical scroll */
.user-status-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: lightgreen;
  font-weight: 600;
  box-shadow: 0 1px 0 #ccc;
}

/* Sticky Sr No. (first column) */
.user-status-table .sticky-sr {
  position: sticky;
  left: 0;
  z-index: 3;
  min-width: 64px;
  width: 64px;
  text-align: center;
  background-color: #fff;
}

.user-status-table thead .sticky-sr {
  top: 0;
  left: 0;
  z-index: 6;
  background-color: lightgreen;
}

.user-status-table tbody .sticky-sr {
  z-index: 3;
}

.user-status-table .even .sticky-sr {
  background-color: #f2f2f2;
}

.user-status-table .odd .sticky-sr {
  background-color: #ffffff;
}

/* Sticky User Name (second column, offset by Sr No width) */
.user-status-table .sticky-name {
  position: sticky;
  left: 64px;
  z-index: 3;
  min-width: 140px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: #fff;
  box-shadow: 4px 0 6px -2px rgba(0, 0, 0, 0.12);
}

.user-status-table thead .sticky-name {
  top: 0;
  left: 64px;
  z-index: 5;
  background-color: lightgreen;
}

.user-status-table tbody .sticky-name {
  z-index: 3;
}

.user-status-table .even .sticky-name {
  background-color: #f2f2f2;
}

.user-status-table .odd .sticky-name {
  background-color: #ffffff;
}

.user-status-table .status-ok,
.user-status-table .status-bad {
  color: #fff;
  text-align: center;
  font-weight: 500;
}

.user-status-table .status-ok { background-color: #03C03C; }
.user-status-table .status-bad { background-color: #EF0107; }
.user-status-table .even { background-color: #f2f2f2; }
.user-status-table .odd { background-color: #ffffff; }

.user-status-empty {
  text-align: center;
  padding: 20px;
  color: #666;
}

@media (max-width: 900px) {
  .user-status-scroll {
    max-height: calc(100dvh - 220px);
    min-height: 520px;
  }

  .user-status-table {
    min-width: 1200px;
  }

  .user-status-table th,
  .user-status-table td {
    padding: 9px 11px;
    font-size: 12.5px;
  }

  .user-status-table .sticky-sr {
    min-width: 56px;
    width: 56px;
  }

  .user-status-table .sticky-name,
  .user-status-table thead .sticky-name {
    left: 56px;
    min-width: 130px;
    max-width: 170px;
  }
}

@media (max-width: 600px) {
  .user-status-scroll {
    max-height: calc(100dvh - 240px);
    min-height: 560px;
    border-radius: 6px;
  }

  .user-status-table {
    min-width: 1150px;
  }

  .user-status-table .sticky-sr {
    min-width: 52px;
    width: 52px;
  }

  .user-status-table .sticky-name,
  .user-status-table thead .sticky-name {
    left: 52px;
    min-width: 120px;
    max-width: 150px;
    font-size: 12px;
  }
}
`;

const existing = document.getElementById('user-status-page-styles');
if (existing) existing.remove();
const style = document.createElement('style');
style.id = 'user-status-page-styles';
style.textContent = css;
document.head.appendChild(style);
